import React, { useCallback } from "react";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Filter,
} from "lucide-react";
import { Button } from "@/components/controls/customControls/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/controls/customControls/select";
import { TimeSlot, useMiniCalendarControl } from "./useMiniCalendarControl";
import useTitanApp from "../../../../hooks/useTitanApp";
import {
  refreshScheduleEventsSilently,
  setShowBookingSlots,
} from "../../../../dispatchers/scheduleEventsDispatchers";
import {
  setCurrentGlobalTabText,
  setCurrentGlobalTabValue,
  setisPatientSearched,
} from "@/dispatchers/appDispatchers";
import { setIsPatientDashboard } from "@/dispatchers/patientDispatchers";
import { useNavigate } from "react-router-dom";
import MiniCalendarSkeleton from "./MiniCalendarSkeleton";
import PatientBookingModal from "@/components/PatientConsultation/components/PatientTemplateDEMDx/components/PatientBookingModal";
import { BlockOutModal } from "@/components/Scheduler/schedule/BlockOutModal";
import {
  useScheduleData,
  blockOutTypes,
} from "@/components/Calendar/scheduleData";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/controls/customControls/alert";
import { AlertCircle } from "lucide-react";

interface MiniCalendarControlProps {
  isDarkMode: boolean;
  onViewFullSchedule?: (date: Date) => void;
  onSlotAction?: (slot: TimeSlot, date: Date) => void;
}

export default function MiniCalendarControl({
  isDarkMode,
  onViewFullSchedule,
  onSlotAction,
}: MiniCalendarControlProps) {
  const state = useMiniCalendarControl();
  const { appState, scheduleEventState, patientState } = useTitanApp();
  const navigate = useNavigate();
  const { currentPharmacyId, globalTabText, features } =
    appState || ({} as any);
  const { selectedPatientDetails } = patientState || ({} as any);
  const { ifWorkForceTabEnabled, ifScheduleTabEnabled } = features;
  const { showBookingSlots, bookingSlotsMode } =
    scheduleEventState || ({} as any);

  const { loading, services, practitioners, slots, slotInterval } =
    state as any;
  // Modal state and schedule actions
  const sched = useScheduleData() as any;
  const {
    pharmacistNames = [],
    roomNames = [],
    servicePharmacists = [],
    availableDates = [],
    getAvailableTimesForDate,
    getFilteredBookingsForDate,
    getFilteredBlockOutsForDate,
    onCreateBooking,
    onEditBooking,
    onEditBlockOut,
    onDeleteBlockOut,
    onPharmacistSelected,
  } = sched || {};
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalMode, setModalMode] = React.useState<"create" | "edit">("create");
  const [initialDate, setInitialDate] = React.useState<string | undefined>(
    undefined,
  );
  const [initialTime, setInitialTime] = React.useState<string | undefined>(
    undefined,
  );
  const [editBooking, setEditBooking] = React.useState<any | undefined>(
    undefined,
  );
  const [initialServiceTypeId, setInitialServiceTypeId] = React.useState<
    string | undefined
  >(undefined);
  const [initialPharmacistName, setInitialPharmacistName] = React.useState<
    string | undefined
  >(undefined);
  const [initialPatientName, setInitialPatientName] = React.useState<
    string | undefined
  >(undefined);
  const [initialPatientId, setInitialPatientId] = React.useState<
    string | undefined
  >(undefined);
  const [initialPatientEmail, setInitialPatientEmail] = React.useState<
    string | undefined
  >(undefined);
  const [initialPatientPhone, setInitialPatientPhone] = React.useState<
    string | undefined
  >(undefined);

  React.useEffect(() => {
    if (showBookingSlots) {
      state.resetToday();
    }
  }, [showBookingSlots, state.resetToday]);

  // When pharmacist is selected for a specific service, load availability
  React.useEffect(() => {
    if (
      state.selectedService &&
      state.selectedService !== "all" &&
      state.selectedPractitioner &&
      state.selectedPractitioner !== "all"
    ) {
      const target = (servicePharmacists || []).find(
        (p: any) =>
          String(p?.name || "") === String(state.selectedPractitioner),
      );
      const pharmId = target?.id ? String(target.id) : undefined;
      if (pharmId) {
        onPharmacistSelected?.(state.selectedService, pharmId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedService, state.selectedPractitioner, servicePharmacists]);

  return (
    <>
      {showBookingSlots && (
        <div className="absolute top-0 left-0 flex items-start justify-center w-full h-full">
          <>
            <div
              className="fixed inset-0 !z-[9998] bg-black/10"
              onClick={state.close}
            />

            <div
              className={`mt-[50px] !z-[9999] ${bookingSlotsMode === "fromAppointment" ? "max-w-[800px] w-full" : "w-96"} rounded-lg border shadow-xl transition-all duration-300 ease-in-out transform ${showBookingSlots ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-2"} ${isDarkMode ? "border-gray-600 bg-gray-800 shadow-black/50" : "border-gray-200 bg-white shadow-gray-900/10"}`}
            >
              <div className="p-4">
                <div
                  className={`flex items-center justify-between ${bookingSlotsMode === "fromAppointment" ? "" : "mb-2"}`}
                >
                  <h3
                    className={`text-lg font-semibold transition-colors duration-300 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {bookingSlotsMode === "fromAppointment"
                      ? "Select Time Slot"
                      : "Schedule"}
                  </h3>
                  <button
                    onClick={state.close}
                    className={`p-1 rounded-md transition-colors duration-300 ${isDarkMode ? "text-gray-400 hover:text-white hover:bg-gray-700" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"}`}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {bookingSlotsMode === "fromAppointment" && (
                  <p className="mb-4 text-sm font-normal">
                    Choose an available time slot to book your appointment
                  </p>
                )}

                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={state.prevDay}
                    className={`p-2 rounded-md transition-colors duration-300 ${isDarkMode ? "text-gray-400 hover:text-white hover:bg-gray-700" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"}`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex flex-col items-center">
                    <div
                      className={`text-sm font-medium transition-colors duration-300 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {state.date.toLocaleDateString("en-US", {
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                      })}
                    </div>
                    <div
                      className={`text-xs transition-colors duration-300 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      {state.date.getFullYear()}
                      {state.date.toDateString() ===
                        new Date().toDateString() && (
                        <span
                          className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${isDarkMode ? "bg-blue-900 text-blue-200" : "bg-blue-100 text-blue-800"}`}
                        >
                          Today
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={state.nextDay}
                    className={`p-2 rounded-md transition-colors duration-300 ${isDarkMode ? "text-gray-400 hover:text-white hover:bg-gray-700" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"}`}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Quick Date Navigation */}
                <div className="mb-4">
                  <div
                    className={`text-xs font-medium mb-2 transition-colors duration-300 ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Quick jump to:
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => state.jumpToDate(7, "days")}
                      className={`px-3 py-1.5 text-xs rounded-md border transition-colors duration-200 ${
                        isDarkMode
                          ? "border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 hover:border-gray-500"
                          : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                      }`}
                    >
                      +7 days
                    </button>
                    <button
                      onClick={() => state.jumpToDate(14, "days")}
                      className={`px-3 py-1.5 text-xs rounded-md border transition-colors duration-200 ${
                        isDarkMode
                          ? "border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 hover:border-gray-500"
                          : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                      }`}
                    >
                      +14 days
                    </button>
                    <button
                      onClick={() => state.jumpToDate(28, "days")}
                      className={`px-3 py-1.5 text-xs rounded-md border transition-colors duration-200 ${
                        isDarkMode
                          ? "border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 hover:border-gray-500"
                          : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                      }`}
                    >
                      +28 days
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <button
                      onClick={() => state.jumpToDate(2, "months")}
                      className={`px-3 py-1.5 text-xs rounded-md border transition-colors duration-200 ${
                        isDarkMode
                          ? "border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 hover:border-gray-500"
                          : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                      }`}
                    >
                      +2 months
                    </button>
                    <button
                      onClick={() => state.jumpToDate(6, "months")}
                      className={`px-3 py-1.5 text-xs rounded-md border transition-colors duration-200 ${
                        isDarkMode
                          ? "border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 hover:border-gray-500"
                          : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                      }`}
                    >
                      +6 months
                    </button>
                  </div>
                </div>
                {loading ? (
                  <MiniCalendarSkeleton isDarkMode={isDarkMode} />
                ) : (
                  <>
                    <div className="flex items-center gap-3 pb-3 mb-4 border-b border-gray-200 dark:border-gray-600">
                      <div className="flex items-center gap-2">
                        <Filter
                          className={`w-4 h-4 transition-colors duration-300 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        />
                        <span
                          className={`text-xs font-medium transition-colors duration-300 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                        >
                          Filter:
                        </span>
                      </div>

                      <Select
                        value={state.selectedService}
                        onValueChange={state.setSelectedService}
                      >
                        <SelectTrigger
                          className={`h-7 text-xs w-full transition-colors duration-300 ${isDarkMode ? "border-gray-600 bg-gray-700 text-gray-200" : "border-gray-300 bg-white text-gray-700"}`}
                        >
                          <SelectValue placeholder="Service" />
                        </SelectTrigger>
                        <SelectContent
                          className={`${isDarkMode ? "bg-gray-800 border-gray-700" : ""} custom-zindex`}
                        >
                          <SelectItem value="all">All Services</SelectItem>
                          {services.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Select
                        value={state.selectedPractitioner}
                        onValueChange={state.setSelectedPractitioner}
                      >
                        <SelectTrigger
                          className={`h-7 !z-[9999] text-xs w-full transition-colors duration-300 ${isDarkMode ? "border-gray-600 bg-gray-700 text-gray-200" : "border-gray-300 bg-white text-gray-700"}`}
                        >
                          <SelectValue placeholder="Practitioner" />
                        </SelectTrigger>
                        <SelectContent
                          className={`${isDarkMode ? "bg-gray-800 border-gray-700" : ""} custom-zindex`}
                        >
                          <SelectItem value="all">All Staff</SelectItem>
                          {practitioners.map((p) => (
                            <SelectItem key={p} value={p}>
                              {p}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {(() => {
                      if (
                        state.selectedService === "all" ||
                        state.selectedPractitioner === "all"
                      )
                        return false;
                      const hasAvailableSlots =
                        (state as any).availableCount > 0 ||
                        slots.some((s: any) => s.status === "available");
                      if (hasAvailableSlots) return false;
                      const y = state.date.getFullYear();
                      const m = String(state.date.getMonth() + 1).padStart(
                        2,
                        "0",
                      );
                      const d = String(state.date.getDate()).padStart(2, "0");
                      const dateStr = `${y}-${m}-${d}`;
                      const times = getAvailableTimesForDate?.(dateStr) || [];
                      return availableDates.length === 0 || times.length === 0;
                    })() && (
                      <Alert variant="destructive" className="mb-4">
                        <AlertCircle className="w-4 h-4" />
                        <AlertTitle>Not available</AlertTitle>
                        <AlertDescription>
                          This pharmacist has no available dates or time slots.
                          Please choose a different pharmacist or service.
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="grid grid-cols-2 gap-3 mb-4 overflow-y-auto max-h-80 scrollbar-hover-thin">
                      {slots.map((slot) => {
                        const y = state.date.getFullYear();
                        const m = String(state.date.getMonth() + 1).padStart(
                          2,
                          "0",
                        );
                        const d = String(state.date.getDate()).padStart(2, "0");
                        const dateStr = `${y}-${m}-${d}`;
                        const intervalMin = slotInterval || 60;

                        const boColor = (() => {
                          const c = (slot as any).color as string | undefined;
                          if (c && typeof c === "string" && c.trim()) return c;
                          const t = (slot as any).type as string | undefined;
                          const fromType = blockOutTypes.find(
                            (x) => x.value === t,
                          )?.color;
                          return fromType || "bg-gray-500";
                        })();

                        const card = (
                          <div
                            key={slot.time}
                            onClick={() => {
                              if (slot.status === "available") {
                                setModalMode("create");
                                setInitialDate(dateStr);
                                setInitialTime(slot.time);
                                setInitialServiceTypeId(
                                  state.selectedService !== "all"
                                    ? state.selectedService
                                    : undefined,
                                );
                                setInitialPharmacistName(
                                  state.selectedPractitioner !== "all"
                                    ? state.selectedPractitioner
                                    : undefined,
                                );
                                if (bookingSlotsMode === "fromAppointment") {
                                  setInitialPatientName(state.selectedPatient);
                                  setInitialPatientId(state.selectedPatientId);
                                  setInitialPatientEmail(
                                    state.selectedPatientDetails.email,
                                  );
                                  setInitialPatientPhone(
                                    state.selectedPatientDetails?.contacts?.find(
                                      (c) => c?.value,
                                    )?.value,
                                  );
                                }
                                setEditBooking(undefined);
                                setModalOpen(true);
                                state.close();
                              } else if (slot.status === "booked") {
                                if (bookingSlotsMode === "fromAppointment") {
                                  return;
                                }

                                const dayBookings =
                                  getFilteredBookingsForDate?.(dateStr) || [];
                                const [sh, sm] = String(slot.time).split(":");
                                const sStart =
                                  parseInt(sh || "0", 10) * 60 +
                                  parseInt(sm || "0", 10);
                                const sEnd = sStart + intervalMin;
                                const match = dayBookings.find((b: any) => {
                                  const [bh, bm] = String(
                                    (b.time || "00:00").slice(0, 5),
                                  ).split(":");
                                  const bStart =
                                    parseInt(bh || "0", 10) * 60 +
                                    parseInt(bm || "0", 10);
                                  const bEnd =
                                    bStart +
                                    (parseInt(String(b.duration || 0), 10) ||
                                      0);
                                  return bStart < sEnd && bEnd > sStart;
                                });
                                if (match) {
                                  setModalMode("edit");
                                  setEditBooking(match);
                                  setInitialDate(match.date);
                                  setInitialTime(
                                    (match.time || "").slice(0, 5),
                                  );
                                  setInitialServiceTypeId(
                                    String(
                                      (match as any).serviceId ||
                                        (match as any).serviceType ||
                                        "",
                                    ),
                                  );
                                  setInitialPharmacistName(
                                    String(match.pharmacist || ""),
                                  );
                                  setModalOpen(true);
                                  state.close();
                                }
                              }
                              onSlotAction?.(slot, state.date);
                            }}
                            className={`p-3 rounded-md border transition-all duration-200 ${
                              slot.status === "booked" &&
                              bookingSlotsMode === "fromAppointment"
                                ? "!cursor-not-allowed"
                                : "cursor-pointer"
                            } ${
                              slot.status === "booked"
                                ? isDarkMode
                                  ? "border-blue-600 bg-blue-900/30 hover:bg-blue-900/50"
                                  : "border-blue-300 bg-blue-50 hover:bg-blue-100"
                                : slot.status === "blocked"
                                  ? `${boColor} relative overflow-hidden border-gray-300`
                                  : isDarkMode
                                    ? "border-gray-600 bg-gray-700 hover:bg-gray-600 hover:border-gray-500"
                                    : "border-gray-200 bg-gray-50 hover:bg-white hover:border-gray-300"
                            }`}
                            style={
                              slot.status === "blocked"
                                ? {
                                    backgroundImage:
                                      "repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.3) 2px, rgba(255,255,255,0.3) 4px)",
                                  }
                                : undefined
                            }
                          >
                            <div
                              className={`text-sm font-medium mb-1 transition-colors duration-300 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {slot.time}
                            </div>
                            {slot.status === "available" ? (
                              <div
                                className={`text-xs transition-colors duration-300 ${isDarkMode ? "text-green-400" : "text-green-600"}`}
                              >
                                Available
                              </div>
                            ) : slot.status === "booked" ? (
                              <div className="space-y-1">
                                <div
                                  className={`text-xs font-medium transition-colors duration-300 ${isDarkMode ? "text-blue-300" : "text-blue-700"}`}
                                >
                                  {(slot as any).patient}
                                </div>
                                <div
                                  className={`text-xs transition-colors duration-300 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
                                >
                                  {(slot as any).service}
                                </div>
                                <div
                                  className={`text-xs transition-colors duration-300 ${isDarkMode ? "text-blue-400" : "text-blue-500"}`}
                                >
                                  {(slot as any).practitioner}
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-1">
                                <div
                                  className={`text-xs font-medium transition-colors duration-300 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                                >
                                  {(slot as any).title}
                                </div>
                              </div>
                            )}
                          </div>
                        );

                        if (slot.status === "blocked") {
                          const dayBlockOuts =
                            getFilteredBlockOutsForDate?.(dateStr) || [];
                          const [sh, sm] = String(slot.time).split(":");
                          const sStart =
                            parseInt(sh || "0", 10) * 60 +
                            parseInt(sm || "0", 10);
                          const sEnd = sStart + intervalMin;
                          const block = (dayBlockOuts || []).find((bo: any) => {
                            const relevant = (() => {
                              if (
                                state.selectedPractitioner &&
                                state.selectedPractitioner !== "all"
                              ) {
                                const list = Array.isArray(
                                  bo?.affectedPharmacists,
                                )
                                  ? bo.affectedPharmacists
                                  : [];
                                return list
                                  .map((p: any) =>
                                    String(p || "").toLowerCase(),
                                  )
                                  .includes(
                                    String(
                                      state.selectedPractitioner || "",
                                    ).toLowerCase(),
                                  );
                              }
                              return true;
                            })();
                            if (!relevant) return false;
                            const [bh, bm] = String(
                              bo.startTime || "00:00",
                            ).split(":");
                            const [eh, em] = String(
                              bo.endTime || "00:00",
                            ).split(":");
                            const bStart =
                              parseInt(bh || "0", 10) * 60 +
                              parseInt(bm || "0", 10);
                            const bEnd =
                              parseInt(eh || "0", 10) * 60 +
                              parseInt(em || "0", 10);
                            return bStart < sEnd && bEnd > sStart;
                          });
                          if (block) {
                            return (
                              <BlockOutModal
                                key={`bo-${slot.time}`}
                                trigger={card}
                                editMode={block}
                                onSave={(data) =>
                                  onEditBlockOut?.(block.id, data, (block as any).otherPharmacyId)
                                }
                                onDelete={(id) => onDeleteBlockOut?.(id, (block as any).otherPharmacyId)}
                                isDarkMode={isDarkMode}
                              />
                            );
                          }
                        }
                        return card;
                      })}
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-600">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const y = state.date.getFullYear();
                            const m = String(
                              state.date.getMonth() + 1,
                            ).padStart(2, "0");
                            const d = String(state.date.getDate()).padStart(
                              2,
                              "0",
                            );
                            const dateStr = `${y}-${m}-${d}`;

                            if (globalTabText === "Calendar") {
                              window.dispatchEvent(
                                new CustomEvent("calendar:openDay", {
                                  detail: { date: dateStr },
                                }),
                              );
                            } else {
                              try {
                                localStorage.setItem(
                                  "calendarRequestedDate",
                                  dateStr,
                                );
                              } catch {}
                              // Compute Calendar tab index based on feature flags
                              const hasDashboard =
                                !!features?.ifPharmacyDashboardEnabled;
                              const hasWorkforce =
                                !!features?.ifWorkForceTabEnabled;
                              const hasSchedule =
                                !!features?.ifScheduleTabEnabled;

                              let calendarIndex = 0;
                              if (hasDashboard) calendarIndex++;
                              calendarIndex++; // Services tab is always there
                              if (hasWorkforce) calendarIndex++;
                              // Calendar tab comes after Workforce if it's the schedule tab
                              // But wait, looking at GlobalTabsSwitcher:
                              // 0: Dashboard (if enabled)
                              // 1: Services
                              // 2: Workforce (if enabled)
                              // 3: Calendar (if enabled)

                              // Let's re-calculate more carefully based on the UI structure:
                              let targetIndex = 0;
                              if (hasDashboard) targetIndex++; // If Dashboard is at 0, next is 1
                              targetIndex++; // Services is always at the next position (0 or 1)
                              if (hasWorkforce) targetIndex++; // Workforce is at the next position
                              // Calendar is at the next position

                              setCurrentGlobalTabValue(targetIndex);
                              setCurrentGlobalTabText("Calendar");
                              setisPatientSearched(false);
                              setIsPatientDashboard(false);
                              if (currentPharmacyId) {
                                navigate(
                                  `/pharmacy/schedule/${currentPharmacyId}`,
                                );
                              }
                            }
                            state.close();
                          }}
                          className={`transition-colors duration-300 ${isDarkMode ? "border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white" : ""}`}
                        >
                          View Full Schedule
                        </Button>
                        {state.date.toDateString() !==
                          new Date().toDateString() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={state.resetToday}
                            className={`transition-colors duration-300 ${isDarkMode ? "text-gray-400 hover:text-white hover:bg-gray-700" : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"}`}
                          >
                            Today
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-2 h-2 rounded-full transition-colors duration-300 ${isDarkMode ? "bg-green-500" : "bg-green-400"}`}
                        ></div>
                        <span
                          className={`text-xs transition-colors duration-300 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {state.availableCount} available, {state.bookedCount}{" "}
                          booked
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        </div>
      )}

      {/* Patient Booking Modal controlled from mini calendar */}
      <PatientBookingModal
        show={modalOpen}
        handleClose={() => {
          setModalOpen(false);
          setEditBooking(undefined);
          setInitialDate(undefined);
          setInitialTime(undefined);
          setInitialServiceTypeId(undefined);
          setInitialPharmacistName(undefined);
          setInitialPatientName(undefined);
          setInitialPatientEmail(undefined);
          setInitialPatientPhone(undefined);
        }}
        patientName={
          initialPatientName ||
          (selectedPatientDetails?.foreName && selectedPatientDetails?.surName
            ? `${selectedPatientDetails.foreName} ${selectedPatientDetails.surName}`.trim()
            : selectedPatientDetails?.patientName) ||
          state?.selectedPatient ||
          ""
        }
        patientId={
          initialPatientId ||
          String(
            selectedPatientDetails?.patientID ||
              selectedPatientDetails?.patientId ||
              state?.selectedPatientId ||
              "",
          )
        }
        email={
          initialPatientEmail ||
          selectedPatientDetails?.email ||
          ""
        }
        mobile={
          initialPatientPhone ||
          selectedPatientDetails?.mobile ||
          selectedPatientDetails?.contacts?.find((c: any) => !!c?.value)?.value ||
          ""
        }
        initialDate={initialDate}
        initialTime={initialTime}
        initialServiceTypeId={initialServiceTypeId}
        initialPharmacistName={initialPharmacistName}
        isDarkMode={isDarkMode}
      />
    </>
  );
}
